import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { reportPeriodEditStore } from '@stores/dictionaryStores/reportPeriodsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { FormWithActions } from '../../FormWithActions';
import { Form } from './Form';
import { validate } from './validateSchema';

export const ReportPeriodEdit = observer(() => {
  const { reportGroupCode, periodCode } = useParams<{ reportGroupCode: string; periodCode: string }>();
  const { push } = useHistory();
  const { setNew, fetch, setEditMode, isLoading, reportPeriod, save, isNew, isEditMode } = reportPeriodEditStore;

  const permission = usePermission('CmReportPeriods', reportPeriodEditStore);

  useEffect(() => {
    if (reportGroupCode !== NEW_ENTITY_ID) {
      fetch(reportGroupCode, periodCode);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, setNew, setEditMode, reportGroupCode, periodCode]);

  const initialValues = useMemo(
    () =>
      isLoading
        ? {}
        : reportPeriod
        ? toJS({
            ...reportPeriod,
            id: `${reportPeriod?.periodCode}-${reportPeriod?.reportGroupCode}`,
            preparationDay: `${reportPeriod?.preparationDay ?? ''}`,
            dueDay: `${reportPeriod?.dueDay ?? ''}`,
          })
        : {},
    [reportPeriod, isLoading],
  );

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);

      if (validation) {
        return validation;
      }

      (async () => {
        const value = await save(values, reportGroupCode, periodCode);
        if (isNew && value?.reportGroupCode && value?.periodCode) {
          push(`${RoutePath.DICTIONARY_REPORT_PERIODS}/${value.reportGroupCode}/${value.periodCode}`);
        }
      })();

      return undefined;
    },
    [isNew, periodCode, permission, push, reportGroupCode, save],
  );

  const goToReportPeriods = useCallback(() => push(RoutePath.DICTIONARY_REPORT_PERIODS), [push]);

  return (
    <DesignLayout title="Справочник: Календарь отчетных форм" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper initialValues={initialValues} onSubmit={handleSubmit} layout="vertical" compact>
        <FormWithActions store={reportPeriodEditStore} goBackLink={goToReportPeriods}>
          <Form />
        </FormWithActions>
      </FormWrapper>
    </DesignLayout>
  );
});
